import { useEffect, useRef, useState } from "react";
import PrivateApi from "../../../services/PrivateApiService";
import { useQuery, useQueryClient } from "react-query";
import SplashPage from "../../../pages/SplashPage";
import ErrorPage from "../../../pages/ErrorPage";
import { Button, Spinner } from "react-bootstrap";
import { FaChevronLeft } from "react-icons/fa";
import { useParams } from "react-router-dom";

/**
 * NotificationDetailedList
 * 
 * List for notification which is all in one solution, without
 * needing to create new page for details of notification.
 * 
 * @param {boolean} isHw - If logged user is housemanagement or not
 * 
 * @returns List
 */
const NotificationDetailedList = ({isHw}) => {

    const API = new PrivateApi(); // API calls 
    const queryClient = useQueryClient(); // query client 
    const { notification_id, secret} = useParams(); // url parameters
    
    const [isMobile, setIsMobile] = useState(false); // is user on mobile phone
    const [collapsed, setCollapsed] = useState(false); // list is collapsed or not
    const [widths, setWidths] = useState({listCol: "30%", listUnCol: "100%", details: "70%"}); // widths for all screens
    const [selected, setSelected] = useState(null); // selected object
    const [markReadLoading, setMarkReadLoading] = useState(false); // is button pressed and waiting for api respond
    const contentRef = useRef(null);

    // get notification data from backend
    const {data, error, isLoading} = useQuery("GET_USER_NOTIFICATION_QUERY", API.get_Notifications, {staleTime: Infinity});


    // Effect that checks is user on mobile phone
    useEffect(() => {
        function checkMobile() {
            setIsMobile(window.innerWidth <= 600);
        }

        checkMobile();

        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, [])


    // Effect if user is on mobile set other widths
    useEffect(() => {
        if (isMobile) {
            setWidths({listCol: "0", listUnCol: "100%", details: "100%"});
        } else {
            setWidths({listCol: "30%", listUnCol: "100%", details: "70%"});
        }
    }, [isMobile])


    // Effect for parameters
    useEffect(() => {
        if (data) {
            if (data.length > 0) setSelected(data[0]);
            setCollapsed(true);
        }

        if (notification_id && data) {
            var findItem = data.filter(d => d.id === Number(notification_id));

            if (findItem.length === 1) {
                setSelected(findItem[0]);
                setCollapsed(true);
            }
        }
    }, [data, notification_id])


    // Effect when something is selected to adjust img and link
    useEffect(() => {
        if (contentRef.current) {
            const images = contentRef.current.querySelectorAll("img");
            const links = contentRef.current.querySelectorAll("a");
            images.forEach(img => img.classList.add("img-fluid"));
            links.forEach(link => link.classList.add("link-energo"));
        }
    }, [selected])

    // Handler for list item click/select
    const listClickHandler = (notification) => {
        if (selected === null || selected.id !== notification.id) {
            setSelected(notification);
            setCollapsed(true);
        }
        else {
            setSelected(null);
            setCollapsed(false);
        }
    }

    // Handler for marking notification as read
    const markAsRead = () => {
        setMarkReadLoading(true);
        API.setRead_Notifications({id: selected.id})
            .then(() => {
                setSelected(prev => ({...prev, is_read: true}));
                queryClient.invalidateQueries({queryKey: ["GET_USER_NOTIFICATION_QUERY"]});
            })
            .catch(err => {
                error = err;
            })
            .finally(() => {
                setMarkReadLoading(false);
            })
    }

    // Handler for downloading attachment
    const downloadAttachment = (e) => {
        e.preventDefault();
        const linkSource = selected.notification.file;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = selected.notification.file_name;
        downloadLink.click();
    }

    // GUI
    if (isLoading) return <SplashPage />
    if (error) return <ErrorPage error={error} />
    return (
        <div className="notifications shadow-lg">
            <ul 
                hidden={collapsed && isMobile}
                style={collapsed? {width: widths.listCol}: {width: widths.listUnCol}} 
                className="notifications-list"
            >
                {data && data.length > 0 ?
                    data.map(n => (
                        <li onClick={() => listClickHandler(n)} key={n.id} className={`${selected !== null && selected.id === n.id? 'selected':''}`}>
                            {n.is_read === false && 
                                <div className="notifications-new pulse"></div>
                            }
                            <span>{n.notification.title}</span> 
                        </li>
                    ))
                    :
                    null
                }
            </ul>
            <div className="notifications-detail" hidden={!collapsed} style={{width: widths.details}}>
                {selected !== null &&
                    <>
                        {isMobile &&
                            <div className="mb-4">
                                <a href="/back" className="link-energo" onClick={e => {e.preventDefault(); setCollapsed(false); setSelected(null)}}>
                                    <FaChevronLeft /> Zurück
                                </a>
                            </div>
                        }
                        <h5>
                            {selected.is_read === false &&
                                <div className="notifications-new"></div>
                            }
                            {selected.notification.title}
                        </h5>
                        <small className="text-muted">{new Date(selected.notification.created).toLocaleDateString('DE-de')}</small>
                        <hr />
                        <p ref={contentRef} dangerouslySetInnerHTML={{__html: selected.notification.message}}></p>
                        {selected.notification.file !== null &&
                            <>
                                <small className="text-muted">Anhang:</small>
                                <p><a onClick={e => downloadAttachment(e)} href="/download" className="link-energo">{selected.notification.file_name}</a></p>
                            </>
                        }
                    </>
                }
                {selected !== null && selected.is_read === false &&
                    <div className="text-end">
                        <hr hidden={isHw && Number(secret) % 3 !== 0} />
                        {markReadLoading ?
                            <Spinner animation="border" className="text-energo" /> :
                            <Button 
                                hidden={isHw && Number(secret) % 3 !== 0}
                                onClick={() => markAsRead()} 
                                className="btn-energo"
                                >
                                    Gelesen
                            </Button>
                        }
                    </div>
                }
                {selected === null && data.length === 0 &&
                    <div style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 15
                    }}>
                        <b className="text-muted">Derzeit sind keine {isHw? "Benachrichtigungen": "News"} vorhanden.</b>
                    </div>
                }
            </div>
        </div>
    )
}
export default NotificationDetailedList;